import { http } from '@/utils/api'
import { withPbx } from '@/stores/network/api'
import * as Sentry from '@/core/sentry'
import { isWhitelistError } from '@/utils/api/whitelist'
import type { Group } from './store'
import type { ProfileColorName } from '@/utils/colors'

export type Extension = {
  id: number
  name?: string
  firstname?: string
  lastname?: string
  trunkId: number
  type:
    | 'default'
    | 'conference'
    | 'fax2mail'
    | 'queue'
    | 'ivr'
    | 'cfc'
    | 'fqdn'
    | 'user'
    | 'group'
    | 'workspace'
  quickDial: string
  directDial: string
  isMsn: boolean
  color: null
  privacy: boolean
  dnd: boolean
  email: null | string
  smsNumber: null | string
  forwardingType: string
  showMissedCalls: boolean
  isClosingActive: boolean
  calleridForwardType: string
  conference: null | {
    id: number
    config: string
  }
  e164: string
  setNameAsPrefix: boolean
  directDialing: string
  devices?: []
  promptForwardingId?: number
  editable: boolean
  isTrunkExisting?: boolean
  isTrunkInPbxMode?: boolean
  trunk?: {
    id: number
    username: string
    label: string
    isBlockNumber: boolean
    isPartialBlock: boolean
    blockRangeStart: number
    blockRangeEnd: number
    blockSize: number
    customer: number
  }
  permissionsToEdit?: []
  onlyForwardExternal: boolean
  blockIncomingExternal: boolean
  ignoreNightswitchInternal: boolean
  allowRefer?: boolean
  groups?: Group[]
  externalReference: string | undefined
  // Ivr
  extendedForwardingProfiles?: {
    id: number
    name: string
    color: ProfileColorName
    isWaitingMusicEnabled: boolean
    extensionId: number
    waitingMusicId: number
    forwardings: {
      ivrSelection: number
      id: number
      name: string
      forwardToExtensionId: string
      ringtime: number
      type: string
      devices: number[]
      externalPhoneNumbers: string[]
      promptId: string
      notificationType: string
      notificationChannel: string
      orderColumn: string
      color: string
      destination?: number
    }[]
    timeslots: {
      day: number
      start: string
      end: string
      profileId: number
    }[]
    ivrTimeout?: number
    defaultIvrExtensionId?: number
  }[]
}

export async function loadExtension(id: number | string) {
  try {
    const url = withPbx(`extensions/${id}`)

    const { data } = await http.get<Extension>(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function createExtension(inputData: any) {
  try {
    const url = withPbx('extensions')

    const { data } = await http.post<Extension>(url, inputData)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updateExtension(id: string | number, inputData: any) {
  try {
    const url = withPbx(`extensions/${id}`)

    const { data } = await http.put<Extension>(url, inputData)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function deleteExtension(id: string) {
  try {
    const url = withPbx(`extensions/${id}`)

    const { data } = await http.delete(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

type Forwarding = {
  type: string
  trigger: string
  period: number
  notificationType: string
  notificationChannel: null
  promptId: number
  phoneNumber: string
  extensionId: number
  updatedAt: string
  createdAt: string
  id: number
  prompt: {
    id: number
    type: string
    color: null
    pbxId: null
    createdAt: null
    updatedAt: null
    name: string
    audioFileName: null
    audioFileSize: null
    audioContentType: null
    audioUpdatedAt: null
    public: null
  }
  extension: Extension
}

export async function saveSimpleForwardings(
  type: 'noAction' | 'waitingMusic' | 'callForwarding',
  extensionId: string,
  forwarding: any
) {
  const url = withPbx(`extensions/${extensionId}/forwardings`)

  const forwardingId = forwarding.id

  const fns = {
    noAction: () => http.delete(`${url}/${forwardingId}`),
    waitingMusic: () =>
      http.put<Forwarding>(`${url}/${forwardingId}`, forwarding),
    callForwarding: () => http.post<Forwarding>(url, forwarding),
  }

  try {
    const response = await fns[type]()

    return {
      status: 'success' as const,
      payload: response,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

type NightSwitch = {
  id: number
  type: string
  period: number
  trigger: string
  notificationType: string
  notificationChannel: null
  phoneNumber: string
  forwardToExtensionId: null
  extensionId: number
  promptId: number
  createdAt: string
  updatedAt: string
}

export async function updateNightSwitch(
  extensionId: string,
  nightSwitchData: any
) {
  try {
    const url = withPbx(`extensions/${extensionId}/closing`)

    const { data } = await http.post<NightSwitch>(url, nightSwitchData)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}
